import assert from '@/sui/tools/assert'
import merge from '@/sui/tools/merge'
import rtti from '@/sui/tools/rtti'
import clone from 'clone'

const defaultPlaceholder = `Taper le chiffre ou quelques lettres du choix.`
const defaultTextPlaceholder = ``
const defaultTooltip = `Taper le chiffre ou quelques lettres du choix.`

class OptionSetter {
  constructor (target, options) {
    assert.notNull(target, `OptionSetter: target should not be null`)
    assert.notNull(options, `OptionSetter: options for target ${target} should not be null`)

    this._t = target
    this._o = options
  }

  apply (list) {
    list.forEach(
      l => {
        this.applyOne(l)
      }
    )
  }

  applyOne (l) {
    assert.notNull(l, `OptionSetter::applyOne: invalid parameter`)

    const o = this._o
    const t = this._t
    if (o[l.sh] !== undefined) {
      if (l.apply == null) {
        t[l.lg] = o[l.sh]
      } else {
        l.apply(t, o[l.sh])
      }
    } else if (t[l.lg] === undefined) {
      t[l.lg] = l.def
    }
  }
}

function applySelect (target, value) {
  assert.notNull(target, 'applySelect: target is null')
  assert.notNull(value, 'applySelect: value is null')

  target.ignore = ignoreFromSelectList
  target.selectList = new rtti.FunctionList([value])
}

function applyShow (target, value) {
  assert.notNull(target, 'applySelect: target is null')
  assert.notNull(value, 'applySelect: value is null')

  target.hide = hideFromShowList
  target.showList = new rtti.FunctionList([value])
}

function applyOptions (f, o = {}) {
  assert.notNull(f, `applyOptions: invalid field`)
  assert.notNull(o, `applyOptions: invalid options object`)
  assert.notNull(f.config, `applyOptions: config should not be null for field ${f}`)

  const dtt = f.type === 'date' ? 'jj/mm/aaaa' : defaultTooltip
  const opts = [
    { sh: 'ro', lg: 'readOnly' },
    { sh: 'sel', apply: applySelect },
    { sh: 'show', apply: applyShow },
    { sh: 'pop', lg: 'pop' },
    { sh: 'cm', lg: 'commit' },
    { sh: 'ini', lg: 'initialize' },
    { sh: 'ctx', lg: 'contextualize' }
  ]

  const copts = [
    { sh: 'tt', lg: 'tooltip', def: dtt },
    { sh: 'op', lg: 'optional' },
    { sh: 'def', lg: 'defaultValue' }
  ]
  if (f.type === 'numeric') {
    copts.push({ sh: 'un', lg: 'unit' })
  } else if (f.type === 'list') {
    copts.push(
      { sh: 'ov', lg: 'optionalValue' },
      { sh: 'dv', lg: 'displayValue', def: true },
      { sh: 'ph', lg: 'placeholder', def: defaultPlaceholder },
      { sh: 'ms', lg: 'matchStart' }
    )
  } else if (f.type === 'text') {
    copts.push({ sh: 'ph', lg: 'placeholder', def: defaultTextPlaceholder })
  }

  const c = f.config
  new OptionSetter(c, o).apply(copts)
  new OptionSetter(f, o).apply(opts)

  const convopts = [
    { sh: 'srv', lg: 'server' },
    { sh: 'sp', lg: 'special' }
  ]
  f.convert = f.convert == null ? {} : f.convert
  new OptionSetter(f.convert, o).apply(convopts)
  return f
}

function buildContext (ctx, sheet) {
  assert.notNull(ctx, `buildContext: ctx is null`)
  assert.notNull(sheet, `buildContext: sheet is null`)

  if (this.contextualize == null) {
    return this.config
  }

  const result = this.contextualize(ctx, sheet)

  for (let key in this.config) {
    if (result[key] == null) {
      result[key] = this.config[key]
    }
  }

  assert.notNull(result.title, `buildContext: empty title`)

  if (process.env.VUE_APP_SPIADI_DEBUG === '1') {
    if (this.type === 'list') {
      const overrides = result.values
      for (let i = 0; i !== overrides.length; ++i) {
        const o = overrides[i]
        assert(
          this.config.values.findIndex(v => v.code === o.code) !== -1,
          `buildContext «${this.config.title}»: invalid override value ${i} (code: ${o.code}, value: '${o.value}').`
        )
      }
    }
  }
  return result
}

function field (k, f, t, o = {}) {
  assert(
    ['bool', 'numeric', 'list', 'date', 'text', 'pickList'].indexOf(k) !== -1,
    `field: invalid type ${k} for field ${f}`
  )
  assert.notNull(f, 'field: missing field identifier')
  assert.notNull(t, 'field: missing field title')

  return applyOptions(
    {
      type: k,
      field: f,
      config: { title: t },
      buildContext: buildContext
    },
    o
  )
}

function numeric (f, t, d, o = {}) {
  assert.notNull(d, `numeric: missing digits for numeric field ${f}`)

  const result = field('numeric', f, t, o)
  result.config.digits = d
  return result
}

function bool (f, t, o = {}) {
  if (o.op == null) {
    o = merge(o, { op: true })
  }

  const result = field('bool', f, t, o)
  return result
}

function list (f, t, v, o = {}) {
  assert.notNull(v, `list: missing values for list field ${f}`)
  assert.isArray(v, `list: v should be an array for list field ${f}`)

  const result = field('list', f, t, o)
  result.config.values = v
  return result
}

function pickList (f, t, v, o = {}) {
  assert.notNull(v, `picklist: missing values for list field ${f}`)
  assert.isArray(v, `pickList: v should be an array for list field ${f}`)

  const result = field('pickList', f, t, o)
  result.config.values = v
  return result
}

function date (f, t, o = {}) {
  assert(t !== null, `date: missing text for date field ${f}`)

  const result = field('date', f, `Date ${t}`, o)
  result.config.text = t
  return result
}

function text (f, t, o = {}) {
  return field('text', f, t, o)
}

function mergeImpl (f, s) {
  return merge(f, s, false)
}

function opts (f, o) {
  assert.notNull(f, `opts: missing initial field`)
  assert.notNull(o, `opts: missing options object`)

  const result = clone(f)
  applyOptions(result, o)
  return result
}

function selectList (list, d, s) {
  assert.notNull(list, `selectList: list is null`)
  assert.isArray(list, `selectList: list should be an array`)
  assert.notNull(d, `selectList: d is null`)
  assert.notNull(s, `selectList: s is null`)

  for (let i = 0; i !== list.length; ++i) {
    const l = list[i]
    if (!l(d, s)) {
      return false
    }
  }

  return true
}

function ignoreFromSelectList (d, s) {
  return !selectList(this.selectList._list, d, s)
}

function hideFromShowList (d, s) {
  return !selectList(this.showList._list, d, s)
}

function select (f, s) {
  assert.notNull(f, `select: missing field`)
  assert.notNull(s, `select: missing selector for field ${field.field}`)

  return opts(f, { sel: s })
}

function not (f) {
  assert.notNull(f, `not: f si null`)
  assert.isFunction(f, `not: f should be a function`)

  const result = (d, s) => !f(d, s)
  Object.defineProperty(result, 'name', { value: `not (${f.name})` })
  return result
}

function formatValue (v) {
  assert.notNull(v)
  if (rtti.isNumber(v) || rtti.isBoolean(v)) {
    return v
  }

  return `"${v}"`
}

function formatList (l) {
  assert.notNull(l)
  assert.isArray(l)
  assert(l.length > 0)

  let result = l
  if (rtti.isString(l[0])) {
    result = result.map(item => `"${item}"`)
  }
  return `[${result.join(', ')}]`
}

function getValue (d, s, f, root = false) {
  assert.isObject(d, `d (${d}) is not an object`)
  assert.isObject(s, `s (${s}) is not an object`)
  assert.isString(f)
  assert.isBoolean(root)

  if (root) {
    return s[f]
  }
  return d[f]
}

function match (f, m, root) {
  assert.notNull(f, `match: missing field identifier`)
  assert.isString(f, `match: invalid field identifier`)
  assert.notNull(m, `match: missing pattern (field ${f})`)

  const result = function (d, s) {
    const e = getValue(d, s, f, root)

    if (e == null) {
      return false
    }

    const matchValue = `${e}`
    return matchValue.match(m) != null
  }
  Object.defineProperty(result, 'name', { value: `(${f} match "${m}")` })
  return result
}

function equals (f, v, root) {
  assert.notNull(f, `equals: missing field identifier`)
  assert.isString(f, `equals: invalid field identifier`)
  assert.notNull(v, `equals: missing value (field ${f})`)

  const result = function (d, s) {
    const e = getValue(d, s, f, root)
    return e === v
  }
  Object.defineProperty(result, 'name', { value: `(${f} === ${formatValue(v)})` })
  return result
}

function equals2 (f, v1, v2, root) {
  assert.notNull(f, 'equals2: missing field identifier')
  assert.isString(f, `equals2: invalid field identifier`)
  assert.notNull(v1, `equals2: first value invalid (field ${f})`)
  assert.notNull(v2, `equals2: second value invalid (field ${f})`)

  const result = (d, s) => {
    const e = getValue(d, s, f, root)
    return e === v1 || e === v2
  }
  Object.defineProperty(
    result, 'name', {
      value: `(${f} === ${formatValue(v1)} || ${f} === ${formatValue(v2)})`
    }
  )
  return result
}

function different (f, v, root) {
  assert.notNull(f, `different: missing field identifier`)
  assert.isString(f, `different: invalid field identifier`)
  assert.notNull(v, `different: missing value (field ${f})`)

  const result = (d, s) => {
    const e = getValue(d, s, f, root)
    return e != null && e !== v
  }
  Object.defineProperty(
    result, 'name', {
      value: `(${f} != null && ${f} !== ${formatValue(v)})`
    }
  )
  return result
}

function different2 (f, v1, v2, root) {
  assert.notNull(f, `different2: missing field identifier`)
  assert.isString(f, `different2: invalid field identifier`)
  assert.notNull(v1, `different2: first value invalid (field ${f})`)
  assert.notNull(v2, `different2: second value invalid (field ${f})`)

  const result = (d, s) => {
    const e = getValue(d, s, f, root)
    return e != null && e !== v1 && e !== v2
  }
  Object.defineProperty(
    result, 'name', {
      value: `(${f} != null && ${f} !== ${formatValue(v1)} && ${f} !== ${formatValue(v2)})`
    }
  )
  return result
}

function inList (f, l, root) {
  assert.notNull(f, `inList: missing field identifier`)
  assert.isString(f, `inList: invalid field identifier`)
  assert.notNull(l, `inList: missing value list (field ${f})`)
  assert.isArray(l, `inList: the value list should be an array (field ${f})`)

  const result = (d, s) => {
    const e = getValue(d, s, f, root)
    return e != null && l.indexOf(e) !== -1
  }
  Object.defineProperty(result, 'name', { value: `(${f} in ${formatList(l)})` })
  return result
}

function inPickList (f, v, root) {
  assert.notNull(f, `inPickList: missing field identifier`)
  assert.isString(f, `inPickList: invalid field identifier`)
  assert.notNull(v, `inPickList: missing sub key`)
  assert.isString(v, `inPickList: invalid sub key`)

  const result = (d, s) => {
    const e = getValue(d, s, f, root)
    return e != null && !!e[v]
  }

  Object.defineProperty(result, 'name', { value: `(${v} picked in ${f})` })
  return result
}

function allTrue (...list) {
  assert(list.length > 0, `allTrue: empty list`)

  let name = ''
  for (let i = 0; i !== list.length; ++i) {
    assert.isFunction(
      list[i],
      `allTrue: entry ${i} is not a function`
    )

    name += list[i].name
    if (i !== list.length - 1) {
      name += ', '
    }
  }

  const result = (d, s) => selectList(list, d, s)
  Object.defineProperty(result, 'name', { value: `allTrue (${name})` })
  return result
}

function oneTrue (...list) {
  assert(list.length > 0, `oneTrue: empty list`)

  let name = ''
  for (let i = 0; i !== list.length; ++i) {
    assert.isFunction(
      list[i],
      `oneTrue: entry ${i} is not a function`
    )

    name += list[i].name
    if (i !== list.length - 1) {
      name += ', '
    }
  }

  const result = (d, s) => {
    for (let i = 0; i !== list.length; ++i) {
      const l = list[i]
      if (l(d, s)) {
        return true
      }
    }
    return false
  }
  Object.defineProperty(result, 'name', { value: `oneTrue (${name})` })
  return result
}

function supOrEqual (f, v, root) {
  assert.notNull(f, `supOrEqual: missing field identifier`)
  assert.isString(f, `supOrEqual: invalid field identifier`)
  assert.notNull(v, `supOrEqual: missing value (field ${f})`)
  assert.isNumber(v)

  const result = (d, s) => {
    const e = getValue(d, s, f, root)
    return e != null && e >= v
  }
  Object.defineProperty(result, 'name', { value: `(${f} != null && ${f} >= ${v})` })
  return result
}

function inf (f, v, root) {
  assert.notNull(f, `inf: missing field identifier`)
  assert.isString(f, `inf: invalid field identifier`)
  assert.notNull(v, `inf: missing value (field ${f})`)
  assert.isNumber(v)

  const result = (d, s) => {
    const e = getValue(d, s, f, root)
    return e != null && e < v
  }
  Object.defineProperty(result, 'name', { value: `(${f} != null && ${f} < ${v})` })
  return result
}

function notNull (f, root) {
  assert.notNull(f, `notNull: missing field identifier`)
  assert.isString(f, `notNull: invalid field identifier`)

  const result = function (d, s) {
    const e = getValue(d, s, f, root)
    return e != null
  }
  Object.defineProperty(result, 'name', { value: `notNull (${f})` })
  return result
}

function isNull (f, root) {
  assert.notNull(f, `isNull: missing field identifier`)
  assert.isString(f, `isNull: invalid field identifier`)

  const result = function (d, s) {
    const e = getValue(d, s, f, root)
    return e == null
  }
  Object.defineProperty(result, 'name', { value: `isNull (${f})` })
  return result
}

function andSelect (f, s) {
  assert.notNull(f, `andSelect: missing field identifier`)
  assert.notNull(f, `andSelect: invalid field identifier`)
  assert.notNull(s, `andSelect: s should not be null (field ${f})`)

  const result = select(f, s)
  if (f.ignore == null) {
    return result
  }
  assert(f.ignore === ignoreFromSelectList)
  assert.notNull(f.selectList)

  for (let i = 0; i !== f.selectList._list.length; ++i) {
    const item = f.selectList._list[i]
    result.selectList.append(item)
  }
  return result
}

function addSelect (condition, fieldList) {
  assert.notNull(condition, `addSelect: missing condition`)
  assert.isFunction(condition, `addSelect: condition should be a function`)
  assert.notNull(fieldList, `addSelect: missing fieldList`)
  assert.isArray(fieldList, `addSelect: fieldList should be an array`)

  const result = []
  for (let i = 0; i !== fieldList.length; ++i) {
    result.push(
      select(fieldList[i], condition)
    )
  }
  return result
}

function key (c) {
  return `<span class="key">${c}</span>`
}

function addMultiline (list) {
  assert.notNull(list)
  assert.isArray(list)

  for (let i = 0; i !== list.length; ++i) {
    const field = list[i]
    assert(field.type === 'text')
    field.config.multiline = true
  }
}

export default {
  key,
  buildContext,
  select,
  andSelect,
  addSelect,
  opts,
  merge: mergeImpl,
  addMultiline,

  cfg: {
    pickList,
    list,
    numeric,
    date,
    text,
    bool
  },

  match,
  equals,
  equals2,
  different,
  different2,
  inList,
  inPickList,
  supOrEqual,
  inf,
  allTrue,
  oneTrue,
  notNull,
  isNull,
  not
}
